import { useRef, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import VisitScheduleForm, { VisitScheduleFormHandle } from 'src/components/VisitScheduleForm';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';

export const Route = createFileRoute('/(app)/onboarding/visit-details')({
    component: VisitDetailsComponent,
});

function VisitDetailsComponent() {
    const {
        canGoBack,
        previousStep,
        nextStep,
        progress,
        invitedTeamMember,
        visitType,
        setVisitScheduleRequest,
    } = useOnboardingWorkflow();

    const isRepeating = visitType === 'repeating';

    const formRef = useRef<VisitScheduleFormHandle>(null);

    const [isFormValid, setIsFormValid] = useState(false);

    const onSubmit = async (data: {
        scheduleStartInstant: number;
        visitDurationMinutes: number;
        cronExpression?: string;
        ianaTimezone?: string;
    }) => {
        setVisitScheduleRequest({
            caregiverRid: invitedTeamMember?.teamMemberRid || '',
            scheduleStartInstant: data.scheduleStartInstant,
            visitDurationMinutes: data.visitDurationMinutes,
            cronExpression: data.cronExpression,
            ianaTimezone: data.ianaTimezone,
        });
        nextStep();
    };

    const handleSubmit = () => {
        formRef.current?.submit();
    };

    return (
        <>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            <Stack sx={{ gap: 4, mb: 2.5 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        {isRepeating
                            ? 'What is their schedule for repeating visits?'
                            : 'When is the next visit?'}
                    </Typography>
                    {!isRepeating && (
                        <Typography variant="body1">
                            Not sure? You can easily remove or update it later from the Visits page.
                        </Typography>
                    )}
                </Stack>

                <VisitScheduleForm
                    ref={formRef}
                    isRepeating={isRepeating}
                    onSubmit={onSubmit}
                    onValidityChange={setIsFormValid}
                    defaultValues={{ caregiverRid: invitedTeamMember?.teamMemberRid || '' }}
                />
            </Stack>
            <PageFooter includeShadow>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                >
                    Next
                </Button>
            </PageFooter>
        </>
    );
}
